/**
 * The round, from the wiki page Architecture → game-core contract, section
 * Round: the leader's action, allies in party order, enemies in id order (each
 * turn followed by the unit's end of turn), the end of round, then the
 * outcome. Round-ending commands validate their input,
 * then call `runRound` with the leader's action. This is the only caller of ai.ts.
 */
import { type AiAction, chooseAction } from './ai.ts';
import { basicAttack, chargeLostFoe, chargeTurn, failRun, maxAp, maxHp, useSkill } from './combat.ts';
import { type ApplyResult, type Ctx, type Step, chain, none } from './commands/types.ts';
import { healQuantity } from './effects.ts';
import type { Event, SkipReason } from './events.ts';
import { gridView } from './floorview.ts';
import { percent } from './determinism.ts';
import { type Dir, add, canStep, cornerClear, dirOffset, dirs, inBounds, samePos } from './grid.ts';
import { nextInt, roll } from './rng.ts';
import {
  InvalidCommand,
  type RunState,
  type Unit,
  assertPhase,
  findUnit,
  hasStatus,
  leaderOf,
  maySwapWith,
  unitById,
  updateUnit,
  withRng,
} from './state.ts';
import { runTurnEnd } from './passives.ts';
import { countDown, interruptCharge, tickStatusDamage } from './status.ts';

/**
 * Checks that the leader may issue `command` now: the run is playing, the
 * leader stands, asleep or stunned leaders may only `wait`, and a charging
 * leader may only `wait` or `turn`. Throws `InvalidCommand` otherwise.
 */
export const assertLeaderMayAct = (state: RunState, command: 'wait' | 'turn' | 'other'): Unit => {
  assertPhase(state, 'playing');
  const leader = leaderOf(state);
  if (leader.down) throw new InvalidCommand('leader_down', 'the leader is down');
  if (command !== 'wait' && (hasStatus(leader, 'sleep') || hasStatus(leader, 'stun'))) {
    throw new InvalidCommand('leader_can_only_wait', 'an asleep or stunned leader can only wait');
  }
  if (command === 'other' && leader.charging !== null) throw new InvalidCommand('charging_leader', 'a charging leader can only wait or turn');
  return leader;
};

// ---------------------------------------------------------------- actions shared by the leader and the AI

/** Faces `dir`; emits `turned` only when the facing changes. */
export const faceUnit = (state: RunState, unitId: string, dir: Dir): ApplyResult => {
  const u = unitById(state, unitId);
  if (u.facing === dir) return none(state);
  return { state: updateUnit(state, unitId, (x) => ({ ...x, facing: dir })), events: [{ type: 'turned', unit: unitId, facing: dir }] };
};

/**
 * The standing party unit a party unit would swap places with by stepping in
 * `dir`, or null (Mystery Dungeon style): the leader swaps with any ally; an
 * ally swaps with an ally earlier in party order (one that has already had
 * its turn this round), never with the leader, so two allies never undo each
 * other's step in the same round. Only when the step would be legal with the
 * other unit out of the way (no wall, no corner cutting). Enemies never swap:
 * every unit blocks them.
 */
export const swapPartner = (state: RunState, unitId: string, dir: Dir): string | null => {
  const u = unitById(state, unitId);
  if (u.side !== 'party') return null;
  const g = gridView(state);
  const to = add(u.pos, dirOffset[dir]);
  const other = g.unitAt(to);
  if (other === null || unitById(state, other).side !== 'party' || !maySwapWith(state, unitId, other)) return null;
  const without = { ...g, unitAt: (p: { x: number; y: number }) => (p.x === to.x && p.y === to.y ? null : g.unitAt(p)) };
  return canStep(without, u.pos, dir) ? other : null;
};

/** Whether `unitId` may step in `dir`: a free tile, or a swap with a party unit (`swapPartner`). */
export const canMove = (state: RunState, unitId: string, dir: Dir): boolean =>
  canStep(gridView(state), unitById(state, unitId).pos, dir) || swapPartner(state, unitId, dir) !== null;

/**
 * The direction a move or basic attack really goes: `dir`, except that a
 * confused unit goes in a random direction (one of the eight) with
 * `confusionChance`%, emitting `action_skipped` with reason `confusion`.
 * Both draws use the `combat` stream; a unit that is not confused draws nothing.
 */
const confusedDir = (ctx: Ctx, state: RunState, unitId: string, dir: Dir): { dir: Dir; result: ApplyResult } => {
  if (!hasStatus(unitById(state, unitId), 'confusion')) return { dir, result: none(state) };
  const r = roll(state.rng.combat, ctx.content.rules.statuses.confusionChance);
  const s = withRng(state, 'combat', r.next);
  if (!r.hit) return { dir, result: none(s) };
  const pick = nextInt(s.rng.combat, dirs.length);
  return {
    dir: dirs[pick.value] ?? dir,
    result: { state: withRng(s, 'combat', pick.next), events: [{ type: 'action_skipped', unit: unitId, reason: 'confusion' }] },
  };
};

/**
 * The unit standing on the tile `unitId` faces, when it is of the same side;
 * else null. The leader's `attack` is refused into such a unit unless the
 * leader is confused.
 */
export const allyAhead = (state: RunState, unitId: string): string | null => {
  const u = unitById(state, unitId);
  const g = gridView(state);
  const pos = add(u.pos, dirOffset[u.facing]);
  const other = inBounds(g, pos) ? g.unitAt(pos) : null;
  return other !== null && unitById(state, other).side === u.side ? other : null;
};

/**
 * True when a standing unit stands on the diagonal tile `unitId` faces but
 * the corner rule (grid.ts `cornerClear`) blocks the attack. The leader's
 * `attack` is then refused unless the leader is confused.
 */
export const blockedByCorner = (state: RunState, unitId: string): boolean => {
  const u = unitById(state, unitId);
  const g = gridView(state);
  const pos = add(u.pos, dirOffset[u.facing]);
  return inBounds(g, pos) && g.unitAt(pos) !== null && !cornerClear(g, u.pos, u.facing);
};

/**
 * A basic attack in `dir`: confusion may redirect it (`confusedDir`), then
 * the unit faces the final direction (`turned` if it changed) and attacks
 * the tile there (combat.ts `basicAttack`). A redirected attack is not
 * checked: it may hit an ally (no SP) or the air.
 */
export const attackUnit = (ctx: Ctx, state: RunState, unitId: string, dir: Dir): ApplyResult => {
  const c = confusedDir(ctx, state, unitId, dir);
  return chain(
    c.result.state,
    (s) => ({ state: s, events: c.result.events }),
    (s) => faceUnit(s, unitId, c.dir),
    (s) => basicAttack(ctx, s, unitId),
  );
};

/**
 * One step in `dir`, facing it. Confusion may send it in a random direction
 * instead (`confusedDir`). A blocked step only turns.
 * A party unit stepping into a party unit it may swap with (`swapPartner`:
 * the leader with any ally, an ally with another ally that is not charging)
 * swaps places with it: both emit `moved`, the stepping unit first; the other
 * keeps its facing. A charging ally the leader swaps with loses its charge
 * (`charge_interrupted` after the two `moved`, nothing spent), since the
 * charge was aimed from the tile it no longer stands on. When the leader's
 * step puts the ally on an unspent AP spring, the ally drinks at once if its
 * AP is below max (`useSpring` with `handoff`: `ap_changed`, `spring_used`,
 * after the moves and any `charge_interrupted`); an ally–ally swap does not.
 */
export const moveUnit = (ctx: Ctx, state: RunState, unitId: string, dir: Dir): ApplyResult => {
  const u = unitById(state, unitId);
  const c = confusedDir(ctx, state, unitId, dir);
  let s = c.result.state;
  const d = c.dir;
  const events: Event[] = [...c.result.events];
  const partner = swapPartner(s, unitId, d);
  if (partner === null && !canStep(gridView(s), u.pos, d)) {
    const turned = faceUnit(s, unitId, d);
    return { state: turned.state, events: [...events, ...turned.events] };
  }
  const to = add(u.pos, dirOffset[d]);
  s = updateUnit(s, unitId, (x) => ({ ...x, pos: to, facing: d }));
  events.push({ type: 'moved', unit: unitId, from: u.pos, to });
  if (partner !== null) {
    s = updateUnit(s, partner, (x) => ({ ...x, pos: u.pos }));
    events.push({ type: 'moved', unit: partner, from: to, to: u.pos });
    const handoff = unitId === leaderOf(s).id;
    const after = chain(
      s,
      (x) => interruptCharge(x, partner),
      (x) => (handoff ? useSpring(ctx, x, partner, 'handoff') : none(x)),
    );
    return { state: after.state, events: [...events, ...after.events] };
  }
  return { state: s, events };
};

/** The kind of an AI action's turn: `wait` for a wait, `walk` for a step (or swap), else `busy`. */
const aiTurnKind = (action: AiAction): TurnKind => (action.kind === 'wait' ? 'wait' : action.kind === 'move' ? 'walk' : 'busy');

const executeAi = (ctx: Ctx, state: RunState, unitId: string, action: AiAction): ApplyResult => {
  switch (action.kind) {
    case 'attack':
      return attackUnit(ctx, state, unitId, action.dir);
    case 'move':
      return moveUnit(ctx, state, unitId, action.dir);
    case 'wait':
      return none(state);
    case 'skill':
      return useSkill(ctx, state, unitId, action.skill, action.dir, action.target);
  }
};

// ---------------------------------------------------------------- one unit's turn

/** Sleep and stun skip the action; paralysis skips it with `paralysisSkipChance`% on the `combat` stream. */
const skipCheck = (ctx: Ctx, state: RunState, u: Unit): { reason: SkipReason | null; state: RunState } => {
  if (hasStatus(u, 'sleep')) return { reason: 'sleep', state };
  if (hasStatus(u, 'stun')) return { reason: 'stun', state };
  if (!hasStatus(u, 'paralysis')) return { reason: null, state };
  const r = roll(state.rng.combat, ctx.content.rules.statuses.paralysisSkipChance);
  return { reason: r.hit ? 'paralysis' : null, state: withRng(state, 'combat', r.next) };
};

/**
 * The kind of a unit's turn: `walk` (a step or a swap), `wait` (a wait; for
 * natural regeneration the same as `walk`, and the only turn that uses an AP
 * spring), `busy` (an attack, a skill, an item, or a charge turn) or
 * `skipped` (sleep, stun, paralysis).
 */
type TurnKind = 'walk' | 'wait' | 'busy' | 'skipped';
type TurnResult = { readonly result: ApplyResult; readonly kind: TurnKind };
type TurnAct = (state: RunState) => TurnResult;

/**
 * What a standing unit does with its turn: skipped by a status, its charge
 * turn, or `act`. An ally (a party unit other than the leader) whose charge
 * was aimed at a foe that has left the skill's shape (combat.ts
 * `chargeLostFoe`) cancels it instead (`charge_interrupted`, nothing spent)
 * and then acts (decided 2026-09-25, Q32); the leader's and enemies' charges
 * fire as they are.
 */
const turnAction = (ctx: Ctx, state: RunState, u: Unit, act: TurnAct): TurnResult => {
  const check = skipCheck(ctx, state, u);
  if (check.reason !== null) {
    return { result: { state: check.state, events: [{ type: 'action_skipped', unit: u.id, reason: check.reason }] }, kind: 'skipped' };
  }
  const ally = u.side === 'party' && u.id !== leaderOf(state).id;
  if (u.charging !== null && ally && chargeLostFoe(ctx, check.state, u.id)) {
    const cancelled = interruptCharge(check.state, u.id);
    const acted = act(cancelled.state);
    return { result: { state: acted.result.state, events: [...cancelled.events, ...acted.result.events] }, kind: acted.kind };
  }
  // A charging unit stands still or fires its charge instead of acting (combat.ts `chargeTurn`).
  // A skipped turn (above) does not advance the charge.
  if (u.charging !== null) return { result: chargeTurn(ctx, check.state, u.id), kind: 'busy' };
  return act(check.state);
};

/** At the end of a sleeper's own turn: `statuses.sleepHeal` of max HP (minimum 1, capped; `healed`, 0 at full). */
const sleepHeal = (ctx: Ctx, state: RunState, unitId: string): ApplyResult => {
  const u = unitById(state, unitId);
  return hasStatus(u, 'sleep') ? healQuantity(ctx, state, unitId, ctx.content.rules.statuses.sleepHeal, 'sleep') : none(state);
};

/**
 * Natural regeneration at the end of a party unit's own turn: a `walk` or `wait` turn
 * adds one to `restTurns`, a `busy` turn sets it to 0, a `skipped` turn leaves
 * it. When it reaches `combat.naturalRegen.everyTurns` it returns to 0 and the
 * unit heals `naturalRegen.hp` (an amount, or a fraction of max HP with
 * minimum 1; × `healing_received_mod`, capped; `healed` with cause `regen`),
 * nothing at full HP. Enemies never regenerate (their `restTurns` stays 0).
 */
const naturalRegen = (ctx: Ctx, state: RunState, unitId: string, kind: TurnKind): ApplyResult => {
  const u = unitById(state, unitId);
  if (u.side !== 'party' || kind === 'skipped') return none(state);
  const { everyTurns, hp } = ctx.content.rules.combat.naturalRegen;
  const rest = kind === 'busy' ? 0 : u.restTurns + 1;
  const due = rest >= everyTurns;
  const s = updateUnit(state, unitId, (x) => ({ ...x, restTurns: due ? 0 : rest }));
  return due && u.hp < maxHp(ctx, u) ? healQuantity(ctx, s, unitId, hp, 'regen') : none(s);
};

const clearTookDamage = (state: RunState, unitId: string): ApplyResult => none(updateUnit(state, unitId, (u) => ({ ...u, tookDamageThisTurn: false })));

/**
 * How a party unit comes to drink from a spring: its own turn's kind (only a
 * `wait` drinks), or `handoff`, the leader's step swapping it onto the spring.
 */
type SpringUse = TurnKind | 'handoff';

/**
 * A party unit that waited on an unspent AP spring (its turn was a `wait`),
 * or that the leader's step swapped onto one (`handoff`, decided 2026-09-25,
 * Q32), with AP below its max, restores the spring's `percent` of its max AP
 * (minimum 1, capped at max; `ap_changed` with the delta), then the spring is
 * spent and leaves the floor (`spring_used`). Any other turn on a spring (a
 * step onto it, an ally–ally swap, an attack, a skill, an item, a charge, a
 * skipped turn), and a wait or handoff at full AP, leave it unspent. Enemies
 * do not use springs.
 */
const useSpring = (ctx: Ctx, state: RunState, unitId: string, kind: SpringUse): ApplyResult => {
  const u = unitById(state, unitId);
  const floor = state.floor;
  const spring = floor?.springs.find((x) => samePos(x.pos, u.pos));
  if ((kind !== 'wait' && kind !== 'handoff') || u.side !== 'party' || floor === null || spring === undefined) return none(state);
  const max = maxAp(ctx, u);
  if (u.ap >= max) return none(state);
  const ap = Math.min(max, u.ap + Math.max(1, percent(max, spring.percent)));
  return {
    state: { ...updateUnit(state, unitId, (x) => ({ ...x, ap })), floor: { ...floor, springs: floor.springs.filter((x) => x !== spring) } },
    events: [
      { type: 'ap_changed', unit: unitId, delta: ap - u.ap, ap },
      { type: 'spring_used', unit: unitId, pos: spring.pos, percent: spring.percent },
    ],
  };
};

/**
 * The end of a unit's own turn, whether it acted, charged or was skipped, in
 * this order: burn tick, poison tick (status.ts `tickStatusDamage`), sleep
 * heal, natural regeneration (`naturalRegen`, by the kind of turn), `turn_end` passives, `tookDamageThisTurn` reset, an AP spring under a
 * party unit that waited (`useSpring`), then the countdown of its statuses and timed
 * stages (status.ts `countDown`). Each step runs only while the unit stands
 * and the run is `playing`: a unit its own burn defeats does nothing more (no
 * heal, no passive, no spring, no countdown), and nothing runs after an
 * escape or the boss's defeat. Counting down at the end of the holder's own
 * turn means an n-turn status (burn and poison included: n ticks) costs the
 * holder exactly n turns, whoever applied it and when.
 */
const endOfTurn = (ctx: Ctx, state: RunState, unitId: string, kind: TurnKind): ApplyResult => {
  const steps: Step[] = [
    (s) => tickStatusDamage(ctx, s, unitId),
    (s) => sleepHeal(ctx, s, unitId),
    (s) => naturalRegen(ctx, s, unitId, kind),
    (s) => runTurnEnd(ctx, s, unitId),
    (s) => clearTookDamage(s, unitId),
    (s) => useSpring(ctx, s, unitId, kind),
    (s) => countDown(s, unitId),
  ];
  const standing = (s: RunState): boolean => {
    const u = findUnit(s, unitId);
    return s.phase === 'playing' && u !== null && !u.down;
  };
  return chain(state, ...steps.map((step): Step => (s) => (standing(s) ? step(s) : none(s))));
};

/** A unit's turn: nothing if it is gone or down; else the status checks, then a charge or `act`; then its end of turn (`endOfTurn`). */
const unitTurn = (ctx: Ctx, state: RunState, unitId: string, act: TurnAct): ApplyResult => {
  const u = findUnit(state, unitId);
  if (u === null || u.down) return none(state);
  const turn = turnAction(ctx, state, u, act);
  const end = endOfTurn(ctx, turn.result.state, unitId, turn.kind);
  return { state: end.state, events: [...turn.result.events, ...end.events] };
};

const aiTurn = (ctx: Ctx, state: RunState, unitId: string): ApplyResult =>
  unitTurn(ctx, state, unitId, (s) => {
    const choice = chooseAction(ctx, s, unitId);
    return { result: executeAi(ctx, choice.state, unitId, choice.action), kind: aiTurnKind(choice.action) };
  });

// ---------------------------------------------------------------- the round

/** `turn` increments, then `round_ended`. */
const endOfRound = (state: RunState): ApplyResult => {
  const turn = state.turn + 1;
  return { state: { ...state, turn }, events: [{ type: 'round_ended', turn }] };
};

/** If the leader is down: the run fails (combat.ts `failRun`: `run_failed`, then the defeat roll on the bag). */
const outcome = (ctx: Ctx, state: RunState): ApplyResult => (leaderOf(state).down ? failRun(ctx, state) : none(state));

/**
 * Like `chain`, but a step runs only while the run is `playing`: once a step
 * ends the run (an escape), the steps after it do not run.
 */
const chainWhilePlaying = (state: RunState, ...steps: readonly Step[]): ApplyResult =>
  chain(state, ...steps.map((step): Step => (s) => (s.phase === 'playing' ? step(s) : none(s))));

/**
 * Runs one round after the leader's command was validated: the leader's
 * action (subject to the status rules; `leaderTurn` is `walk` for `move`, `wait`
 * for `wait`, else `busy`, for natural regeneration and AP springs), each standing ally in party order,
 * each enemy in id order (via ai.ts), each followed by its end of turn
 * (`endOfTurn`), the end of round (`endOfRound`), and the outcome.
 * Units removed or downed earlier in the round do not act. After each unit's
 * turn, if the run is no longer `playing` (the leader escaped, or the boss fell), the round
 * stops there: no further turns, no end of round, no `round_ended`.
 */
export const runRound = (ctx: Ctx, state: RunState, leaderAction: Step, leaderTurn: 'walk' | 'wait' | 'busy'): ApplyResult => {
  const leaderId = leaderOf(state).id;
  const allyIds = state.party.slice(1);
  return chainWhilePlaying(
    state,
    (s) => unitTurn(ctx, s, leaderId, (x) => ({ result: leaderAction(x), kind: leaderTurn })),
    ...allyIds.map((id): Step => (s) => aiTurn(ctx, s, id)),
    (s) => chainWhilePlaying(s, ...s.units.filter((u) => u.side === 'enemy').map((u): Step => (x) => aiTurn(ctx, x, u.id))),
    (s) => endOfRound(s),
    (s) => outcome(ctx, s),
  );
};
